<template>
  <div>
    <div class="d-flex justify-space-between">
      <v-breadcrumbs
        v-if="breadcrumbs"
        :items="breadcrumbs"
        color="primary-darken-1"
        active-color="black"
        density="compact"
        class="pl-0"
        divider="›"
      />

      <div>
        <v-btn
          v-if="helpLink && isHelpPathVisible(helpLink)"
          v-tooltip="t('global.help')"
          :to="helpLink"
          color="primary"
          size="small"
          icon="fas fa-circle-question"
        />
        <v-btn
          v-if="adminHelpLink && isHelpPathVisible(adminHelpLink)"
          v-tooltip="t('global.admin_help')"
          :to="adminHelpLink"
          size="small"
          color="primary"
          icon="fas fa-circle-question"
        />
        <v-btn
          v-if="pageLink"
          v-tooltip="t('global.go_to_page')"
          size="small"
          :to="pageLink"
          color="primary"
          icon="fas fa-magnifying-glass"
        />
      </div>
    </div>
    <v-divider
      class="my-2"
      thickness="2"
      opacity="100"
    />

    <global-header
      :title="title"
      :doc-header="!!docType"
    >
      <template #append>
        <slot name="append" />
      </template>
    </global-header>

    <div
      v-if="!hideTopics && helpPageTopics.length > 1"
      class="mb-5"
    >
      <div class="text-body-large">
        {{ t('documentation.on_this_page') }}
      </div>
      <doc-ul>
        <doc-li
          v-for="(topic, index) in helpPageTopics"
          :key="index"
        >
          <a
            class="text-decoration-none text-primary"
            :href="`#${topic.route}`"
          >{{ topic.title }}</a>
        </doc-li>
      </doc-ul>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { PropType } from 'vue';
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useHelpPages } from '@/composables/useHelpPages';
import GlobalHeader from './GlobalHeader.vue';

const props = defineProps({
  items: {
    type: Array as PropType<BreadCrumb[]>,
    required: true,
  },
  admin: {
    type: Boolean,
    default: false,
  },
  maintenance: {
    type: Boolean,
    default: false,
  },
  docType: {
    type: String,
    required: false,
  },
  helpLink: {
    type: String,
    required: false,
  },
  adminHelpLink: {
    type: String,
    required: false,
  },
  pageLink: {
    type: String,
    required: false,
  },
  /**
   * Hides the "Topics on this help page" list, for help pages short enough not to need it.
   */
  hideTopics: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();
const { isHelpPathVisible } = useHelpPages();

const homeCrumb = computed<BreadCrumb>(() => ({
  title: t('global.home'),
  to: '/',
}));

const adminCrumb = computed<BreadCrumb>(() => ({
  title: t('global.admin'),
  to: '/admin',
}));

const maintenanceCrumb = computed<BreadCrumb>(() => ({
  title: t('maintenance.title'),
  to: '/maintenance',
}));

const docCrumb = computed<BreadCrumb>(() => {
  const docTypeMap: Record<string, string> = {
    publisher: 'global.documentation_publisher',
    maintenance: 'global.documentation_maintenance',
    admin: 'global.documentation_admin',
  };

  return {
    title: t((props.docType && docTypeMap[props.docType]) || 'global.documentation'),
    to: `/docs/${props.docType || ''}`,
  };
});

const breadcrumbs = computed(() => {
  const crumbs = [
    homeCrumb.value,
    ...props.admin
      ? [adminCrumb.value]
      : [],
    ...props.maintenance
      ? [maintenanceCrumb.value]
      : [],
    ...props.docType
      ? [docCrumb.value]
      : [],
    ...props.items.map((c) => ({
      ...c,
      active: false,
    })),
  ];

  const last = crumbs.at(-1);
  if (last) {
    last.active = true;
  }

  return crumbs;
});

const helpPageTopics = ref<Array<{ route: string; title: string }>>([]);
onMounted(() => {
  getHeadlines();
});

async function getHeadlines() {
  await new Promise(r => setTimeout(r, 50));
  const result = document.getElementsByClassName('subheadinglink');
  const arr: any = Array.from(result);
  helpPageTopics.value = arr.map((a: HTMLAnchorElement) => ({
    route: a.name,
    title: a.textContent?.trim() ?? '',
  }));
}

const title = computed(() => {
  const crumbs = breadcrumbs.value;
  return crumbs.at(-1)?.title ?? '';
});
</script>
